import React, { FC } from 'react';

import { styled } from '@mui/material';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import ZoomedBackground, { IMAGE_ZOOM_ON_HOVER } from '~/components/ZoomedBackground';
import { SMALL_MARGIN_PX, SMALL_RADIUS_PX } from '~/theme';

const sizeMap = {
  sm: 60,
  md: 80,
  lg: 92,
  ml: 120,
};

type Size = keyof typeof sizeMap;

interface IAvatar {
  src?: string | null;
  loading?: boolean;
  alt?: string | null;
  size: Size;
  noBorder?: boolean;
  squared?: boolean;
  transformation?: Array<Record<string, string | number>>;
  hoverEffect?: boolean;
}

type Dimension = { width: number; height: number };

const dimensions = Object.fromEntries(
  Object.entries(sizeMap).map(([key, value]) => [key, { width: value, height: value }]),
) as Record<Size, Dimension>;

const Avatar: FC<IAvatar> = ({
  loading,
  src,
  size,
  noBorder = false,
  squared,
  hoverEffect = true,
  ...rest
}) => {
  const dimension = dimensions[size];

  if (loading) {
    return (
      <LoadingAvatar
        className={rest['className']}
        width={dimension.width}
        height={dimension.height}
        squared={squared}
      />
    );
  }

  return (
    <AvatarWithZoom
      className={rest['className']}
      width={dimension.width}
      height={dimension.height}
      noBorder={noBorder}
      squared={squared}
      path={src}
      hoverEffect={hoverEffect}
      srcset={[{ ...dimension, crop: 'maintain_ratio', focus: 'auto' }]}
      sizes={`${sizeMap[size]}px`}
      draggable={false}
      {...rest}
    />
  );
};

export default Avatar;

const createAvatar = (tag) => styled(tag, {
  shouldForwardProp: (prop) => prop !== 'squared' && prop !== 'width' && prop !== 'height',
})<{ width: number; height: number; squared?: boolean }>`
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  border-radius: ${({ squared }) => (squared ? SMALL_RADIUS_PX : '50%')};
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const AvatarWithZoom = styled(createAvatar(ZoomedBackground), {
  shouldForwardProp: (prop) => prop !== 'noBorder' && prop !== 'hoverEffect',
})<{ noBorder?: boolean; hoverEffect: boolean }>`
  border: ${({ theme, noBorder }) => !noBorder && `2px solid ${theme.palette.common.white}`};
  overflow: hidden;

  &:hover .${IMAGE_ZOOM_ON_HOVER} {
    transform: ${({ hoverEffect }) => !hoverEffect && 'none'};
  }
`;
const LoadingAvatar = createAvatar(AnimatedSkeleton);
